import { useState } from "react";
import {
  trackRedirectPopupAction,
  trackRedirectPopupDontShow,
} from "../utils/analytics";

function RedirectPopup({
  isOpen,
  onClose,
  onConfirm,
  pasteShortcut,
  sessionId,
  topicName,
  promptConfig,
}) {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleDontShowChange = (e) => {
    const newValue = e.target.checked;
    setDontShowAgain(newValue);
    trackRedirectPopupDontShow(sessionId, newValue);
  };

  const handleClose = () => {
    trackRedirectPopupAction(sessionId, "cancel");
    onClose();
  };

  const handleConfirm = () => {
    trackRedirectPopupAction(sessionId, "confirm");
    onConfirm(dontShowAgain);
  };

  if (!isOpen) return null;

  const contentTypes = Object.entries(promptConfig.options)
    .filter(([_, enabled]) => enabled)
    .map(([key]) => {
      switch (key) {
        case "summary":
          return "a summary";
        case "shortSummary":
          return "a short summary";
        case "article":
          return "an article";
        case "quotes":
          return "quotes";
        case "table":
          return "a table";
        case "keyPoints":
          return "key points";
        case "diagram":
          return "a diagram";
        case "questions":
          return "a quiz session";
        case "flashcards":
          return "flashcards";
        case "faq":
          return "FAQs";
        case "qa":
          return "Q&A";
        default:
          return key;
      }
    });

  const contentList =
    contentTypes.length > 1
      ? contentTypes.slice(0, -1).join(", ") + " and " + contentTypes.slice(-1)
      : contentTypes[0];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex flex-col items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full shadow-xl">
        <div className="mb-4 text-teal-600 dark:text-teal-400">
          <svg
            className="w-12 h-12 mx-auto"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 text-center">
          Prompt Copied!
        </h3>
        <p className="text-gray-600 dark:text-gray-300 text-center mb-6">
          You will generate {contentList} about{" "}
          <span className="font-semibold text-gray-800 dark:text-gray-200">
            {topicName}
          </span>
          . The instructions will be copied to your clipboard and you will need
          to paste them into ChatGPT.
        </p>
        <div className="flex items-center justify-center gap-2 mb-2">
          <input
            type="checkbox"
            id="dontShowAgain"
            checked={dontShowAgain}
            onChange={handleDontShowChange}
            className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
          />
          <label
            htmlFor="dontShowAgain"
            className="text-sm text-gray-600 dark:text-gray-400"
          >
            Don't show again
          </label>
        </div>
        {dontShowAgain ? (
          <div className="mb-6 p-3 bg-teal-50 dark:bg-teal-900/30 border border-teal-200 dark:border-teal-700 rounded-lg">
            <p className="text-sm text-teal-800 dark:text-teal-200">
              From now on, clicking on a topic will automatically copy the
              instructions and open ChatGPT.
            </p>
          </div>
        ) : (
          <div className="mt-7"></div>
        )}
        <div className="flex gap-3 justify-center">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            Copy Instructions + Open ChatGPT
          </button>
        </div>
      </div>
      <div className="-mt-4 max-w-[200px] ml-[100px] text-center">
        <svg
          className="w-6 h-6 mx-auto mt-2 text-gray-400 dark:text-gray-500 animate-bounce"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={3}
            d="M5 10l7-7m0 0l7 7m-7-7v18"
          />
        </svg>
        <div className="text-sm text-white dark:text-gray-400">
          After clicking, remember to paste ({pasteShortcut}) the instructions
          into the GPT
        </div>
      </div>
    </div>
  );
}

export default RedirectPopup;
